/**
 * Cookie Category Component
 * A card displaying a single cookie category with toggle and expandable description
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CookieCategory as CookieCategoryType } from '../types';
import { DIALOG_CONTENT } from '../constants';
import { CookieToggle } from './CookieToggle';

interface CookieCategoryProps {
  /** Category configuration */
  category: CookieCategoryType;
  /** Current checked state */
  checked: boolean;
  /** Called when toggle changes */
  onChange: (checked: boolean) => void;
}

export function CookieCategory({ category, checked, onChange }: CookieCategoryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasExtendedDescription = Boolean(category.extendedDescription);

  return (
    <div
      className={cn(
        'group rounded-2xl border transition-all duration-300',
        'bg-white hover:shadow-md',
        checked || !category.canToggle
          ? 'border-[#55a5db]/30 hover:border-[#55a5db]/50'
          : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <div className="p-4 tablet:p-5">
        {/* Header row with title and toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm tablet:text-base font-semibold text-gray-900 leading-tight">
              {category.title}
            </h3>
            <p className="mt-1.5 text-xs tablet:text-sm text-gray-600 leading-relaxed">
              {category.description}
            </p>
          </div>

          {/* Toggle - visible on tablet+ in header */}
          <div className="hidden tablet:block shrink-0">
            <CookieToggle
              checked={checked}
              onChange={onChange}
              disabled={!category.canToggle}
              label={`${category.title} cookies`}
            />
          </div>
        </div>

        {/* Toggle - visible on mobile below description */}
        <div className="tablet:hidden mt-3">
          <CookieToggle
            checked={checked}
            onChange={onChange}
            disabled={!category.canToggle}
            label={`${category.title} cookies`}
          />
        </div>

        {/* Read more button */}
        {hasExtendedDescription && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'mt-3 flex items-center gap-1.5 text-xs tablet:text-sm font-medium transition-colors duration-200',
              'text-[#55a5db] hover:text-[#3d8bc2]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#55a5db]/50 focus-visible:ring-offset-2 rounded'
            )}
            aria-expanded={isExpanded}
          >
            <span>{isExpanded ? DIALOG_CONTENT.readLess : DIALOG_CONTENT.readMore}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-300',
                isExpanded && 'rotate-180'
              )}
            />
          </button>
        )}

        {/* Extended description */}
        <div
          className={cn(
            'grid transition-all duration-300 ease-out',
            isExpanded ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            <div className="text-xs tablet:text-sm text-gray-600 leading-relaxed whitespace-pre-line border-t border-gray-100 pt-3">
              {category.extendedDescription}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
